import { SelectionModel } from '@angular/cdk/collections';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling';
import { type AfterViewInit, ChangeDetectorRef, type ElementRef, type OnInit } from '@angular/core';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  ViewChild,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PalantirButtonComponent } from '../button/button.component';
import { PalantirCheckboxComponent } from '../checkbox/checkbox.component';
import { PalantirDialogRef } from '../dialog/dialog-ref.service';
import { PalantirDialogComponent } from '../dialog/dialog.component';
import { PalantirInputComponent } from '../input/input.component';
import { PalantirSkeletonComponent } from '../skeleton/skeleton.component';
import { PalantirTextComponent } from '../text/text.component';
import { PalantirSelectableItemComponent } from './components/selectable-item/selectable-item.component';
import { selectItemDialogLogger } from './logger.instance';
import type {
  PalantirSelectItemDialogItem,
  PalantirSelectItemDialogOptions,
} from './select-item-dialog.type';

const logger = selectItemDialogLogger.extend('component');

@Component({
  selector: 'palantir-select-item-dialog',
  templateUrl: './select-item-dialog.component.html',
  styleUrls: ['./select-item-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PalantirDialogComponent,
    PalantirTextComponent,
    PalantirButtonComponent,
    PalantirInputComponent,
    PalantirCheckboxComponent,
    PalantirSkeletonComponent,
    PalantirSelectableItemComponent,
    ReactiveFormsModule,
    FormsModule,
    ScrollingModule,
    ExperimentalScrollingModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PalantirSelectItemDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('content') private content: ElementRef<HTMLDivElement> | null = null;

  public items = signal<PalantirSelectItemDialogItem[]>([]);

  public allOriginalItems = signal<any[]>([]);

  public allItems = signal<PalantirSelectItemDialogItem[]>([]);

  public searchControl = new FormControl('');

  public isScrolling = signal(false);

  public limitItems = signal<number>(0);

  public model: SelectionModel<number | string>;

  private isLoadingMore = signal<boolean>(false);

  private skip = signal<number>(0);

  public isLoading = signal<boolean>(false);

  public constructor(
    @Inject(DestroyRef) public destroyRef: DestroyRef,
    private readonly palantirDialogRef: PalantirDialogRef,
    @Inject(DIALOG_DATA) public data: PalantirSelectItemDialogOptions<any>,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.allOriginalItems.set(this.data.items);
    this.items.set(this.data.items.map((item: any) => this.data.map(item)));
    this.allItems.set(this.getFlatItems(this.data.items));
    this.limitItems.set(this.data.limitItems ?? 0);

    this.model = new SelectionModel<number | string>(this.data.type === 'multiple', []);

    logger.debug('Initialized', {
      type: this.data.type,
      itemsCount: this.data.items.length,
      limitItems: this.limitItems(),
    });
  }

  public ngOnInit() {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (value: string | null) => {
        if (this.data.infiniteScroll?.enabled) {
          this.skip.set(0);
        }

        let items = await this.data.search(value);
        items = items.map((item: any) => this.data.map(item));

        this.allOriginalItems.update((originalItems) => {
          const newUniqueItems = items.filter(
            (newItem) =>
              !originalItems.some(
                (existingItem) =>
                  existingItem[this.data.itemKey ?? 'id'] === newItem[this.data.itemKey ?? 'id'],
              ),
          );

          return originalItems.concat(newUniqueItems);
        });

        this.items.set(items);

        logger.info('Search results updated', {
          query: value,
          resultsCount: items.length,
        });
      });

    const allSelectedItems = this.getAllSelectedItems();
    this.model.select(...allSelectedItems.map((item) => item.id));
  }

  public ngAfterViewInit(): void {
    this.setupScrollListener();
  }

  public close() {
    logger.info('Dialog closed');
    void this.palantirDialogRef.close({ role: 'cancel' });

    this.changeDetectorRef.markForCheck();
  }

  public confirm() {
    const selectedItems = this.filterItemsRecursively(this.getOriginalItems());

    logger.info('Selection confirmed', {
      selectedCount: selectedItems.length,
      selectedIds: this.model.selected,
    });

    void this.palantirDialogRef.close({
      role: 'success',
      data: selectedItems,
    });

    this.changeDetectorRef.markForCheck();
  }

  public isAllSelected() {
    return this.getFlatItems(this.items()).every((item: any) =>
      this.model.selected.includes(item[this.data.itemKey ?? 'id']),
    );
  }

  public toggleAll() {
    const flatItems = this.getFlatItems(this.items());
    const items = flatItems.filter((item: any) => !item.disabled);

    if (this.isAllSelected()) {
      logger.debug('Deselected all items');
      this.model.deselect(...items.map((item: any) => item[this.data.itemKey ?? 'id']));
      return;
    }

    if (this.limitItems() && this.model.selected.length >= this.limitItems()) {
      logger.debug('Reached limit of selected items', { limit: this.limitItems() });
      this.model.deselect(...items.map((item: any) => item[this.data.itemKey ?? 'id']));

      return;
    }

    if (this.limitItems()) {
      logger.debug('Selecting items up to limit', { limit: this.limitItems() });
      this.model.select(
        ...items.slice(0, this.limitItems()).map((item: any) => item[this.data.itemKey ?? 'id']),
      );

      return;
    }

    logger.debug('Selected all items');
    this.model.select(...items.map((item: any) => item[this.data.itemKey ?? 'id']));
  }

  public get title(): string {
    let title = this.data.title;

    if (this.data.type === 'multiple') {
      title += ` (${this.model.selected.length}${this.limitItems() ? '/' + this.limitItems() : ''})`;
    }

    return title;
  }

  private getOriginalItems(): any[] {
    return this.filterItemsRecursively(this.allOriginalItems());
  }

  private filterItemsRecursively(items: any[]): any[] {
    return items.filter((item: any) => {
      if (item.children?.length) {
        item.children = this.filterItemsRecursively(item.children);
      }

      return this.model.selected.includes(item[this.data.itemKey ?? 'id']);
    });
  }

  private getFlatItems(items: any[]): any[] {
    const flatItems: any[] = [];
    for (const item of items) {
      flatItems.push(item);

      if (item.children) {
        flatItems.push(...this.getFlatItems(item.children));
      }
    }

    return flatItems;
  }

  private getAllSelectedItems(): any[] {
    const items = this.getFlatItems(this.items());

    return items.filter((item) => item.checked);
  }

  public trackByFn(index: number, item: PalantirSelectItemDialogItem): number | string {
    return item.id;
  }

  private setupScrollListener(): void {
    if (!this.content?.nativeElement) return;

    const scrollContainer = this.content.nativeElement;

    scrollContainer.onscroll = async () => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;

      this.isScrolling.set(scrollTop >= 60);

      if (this.data.infiniteScroll?.enabled && scrollTop + clientHeight >= scrollHeight - 100) {
        await this.loadMoreItems();
      }
    };
  }

  private async loadMoreItems() {
    try {
      if (this.isLoadingMore() || !this.data.infiniteScroll?.enabled) return;

      this.isLoadingMore.set(true);
      this.isLoading.set(true);

      const take = this.data.infiniteScroll.take ?? 20;
      const currentCount = this.items().length;

      logger.debug('Loading more items', { currentCount, take, search: this.searchControl.value });

      const newItems = await this.data.infiniteScroll!.loadMore(
        currentCount,
        take,
        this.searchControl.value,
      );

      const mappedItems = newItems.map((item) => this.data.map(item));

      this.allOriginalItems.update((orig) => [...orig, ...newItems]);
      this.items.update((curr) => [...curr, ...mappedItems]);
      this.allItems.update((curr) => [...curr, ...mappedItems]);

      logger.info('More items loaded', {
        newItemsCount: newItems.length,
        totalItemsCount: this.items().length,
      });

      this.isLoadingMore.set(false);
      this.isLoading.set(false);
    } catch (error) {
      logger.error('Failed to load more items', error);
    }
  }
}
